import { Brain, Sparkles, ArrowRight, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '../components/SEOHead';
import { useLanguage } from '../lib/i18n/context';

export function ABCMethodPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  return (
    <>
      <SEOHead 
        title={t('abc.title') + ' | ABCbrain.xyz'}
        description={t('abc.subtitle')}
        canonicalPath={language === 'de' ? '/de/die-ABC-Listen-Methode' : '/en/abc-method'}
      />
      
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Brain className="h-16 w-16 text-primary-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('abc.title')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('abc.subtitle')}
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <p className="lead text-gray-700">
              {t('abc.intro')}
            </p>

            <div className="mt-8 flex justify-center">
              <Link
                to="/"
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 p-[2px] transition-all hover:from-accent-600 hover:to-primary-600 active:scale-[0.98]"
              >
                <div className="absolute inset-0 bg-[length:200%_100%] bg-gradient-to-r from-accent-500/0 via-white/25 to-accent-500/0 group-hover:animate-shimmer" />
                <div className="relative flex items-center gap-3 rounded-[6px] bg-gradient-to-br from-accent-500 to-primary-500 px-8 py-4">
                  <Rocket className="h-6 w-6 text-white transition-transform group-hover:scale-110 group-hover:animate-pulse" />
                  <span className="text-lg font-medium text-white">{t('abc.start.button')}</span>
                </div>
              </Link>
            </div>
          </div>

          <section className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('abc.how_it_works.title')}
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('abc.how_it_works.step1.title')}</h3>
                  <p className="text-gray-700">{t('abc.how_it_works.step1.description')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('abc.how_it_works.step2.title')}</h3>
                  <p className="text-gray-700">{t('abc.how_it_works.step2.description')}</p>
                  <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-800 mb-2">{t('abc.how_it_works.step2.example.title')}</p>
                    <ul className="space-y-2">
                      <li><strong>A:</strong> {t('abc.how_it_works.step2.example.a')}</li>
                      <li><strong>B:</strong> {t('abc.how_it_works.step2.example.b')}</li>
                      <li><strong>C:</strong> {t('abc.how_it_works.step2.example.c')}</li>
                      <li><strong>D:</strong> {t('abc.how_it_works.step2.example.d')}</li>
                      <li>...</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('abc.how_it_works.step3.title')}</h3>
                  <p className="text-gray-700">{t('abc.how_it_works.step3.description')}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('abc.why_effective.title')}
            </h2>
            <p className="text-gray-700">
              {t('abc.why_effective.description')}
            </p>
          </section>

          <section className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="h-8 w-8 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900">
                {t('abc.ai_support.title')}
              </h2>
            </div>
            <p className="text-gray-700">
              {t('abc.ai_support.description')}
            </p>
          </section>

          <section className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('abc.ready.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('abc.ready.description')}
            </p>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-50 to-accent-50 p-8">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-accent-500" />
              <div className="relative text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('abc.cta.title')}
                </h3>
                <Link
                  to="/"
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 p-[2px] transition-all hover:from-accent-600 hover:to-primary-600 active:scale-[0.98]"
                >
                  <div className="absolute inset-0 bg-[length:200%_100%] bg-gradient-to-r from-accent-500/0 via-white/25 to-accent-500/0 group-hover:animate-shimmer" />
                  <div className="relative flex items-center gap-3 rounded-[6px] bg-gradient-to-br from-accent-500 to-primary-500 px-8 py-4">
                    <Sparkles className="h-6 w-6 text-white transition-transform group-hover:scale-110 group-hover:animate-pulse" />
                    <span className="text-lg font-medium text-white">{t('abc.start.button')}</span>
                  </div>
                </Link>
              </div>
              <div className="absolute -bottom-6 -right-6 opacity-10">
                <Brain className="h-32 w-32 text-primary-900" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}